import { useEffect, useMemo, useRef, useState } from 'react'

import Button from 'components/global/Button'
import Input from 'components/global/Input'
import Select from 'components/global/Select'
import Footer from 'components/internal/Footer'
import Header from 'components/internal/Header'
import CompletedLink from 'components/demo/CompletedLink'

export default function Demo({ demoIntegrationKey, embedHost }) {

  // Ref 
  const Covie = useRef(null)

  // Local state
  const [sdkLoaded, setSdkLoaded] = useState(false)
  const [sdkInitialized, setSdkInitialized] = useState(false)
  const [demoState, setDemoState] = useState('CONFIG')
  const [linkId, setLinkId] = useState('')
  const [currentStep, setCurrentStep] = useState('')
  const [linkError, setLinkError] = useState(null)
  const [policies, setPolicies] = useState([])
  const [buttonOptions, setButtonOptions] = useState([
    { label: 'Covie', value: 'covie' },
    { label: 'Blue', value: 'blue' },
    { label: 'Green', value: 'green' },
    { label: 'Black', value: 'black' },
    { label: 'White', value: 'white' },
  ])
  const [selectedButtonOption, setSelectedButtonOption] = useState(
    buttonOptions[0]
  )

  // Default config - this is configured to show the lightbox version
  const defaultConfiguration = {
    elementId: 'covie-demo',
    buttonSize: 'md',
    buttonTheme: selectedButtonOption.value,
    embed: {
      integrationKey: demoIntegrationKey,
      linkId: '',
    },
  }

  // State that relies on config
  const [configuration, setConfiguration] = useState(defaultConfiguration)

  // Computations
  const isIntegrationKeyValid = useMemo(
    () =>
      configuration.embed.integrationKey &&
      configuration.embed.integrationKey.substr(0, 3) === 'ik_' &&
      configuration.embed.integrationKey.length >= 9,
    [configuration]
  )
  const hideSDK = useMemo(() => !isIntegrationKeyValid, [isIntegrationKeyValid])

  /**
   * On configuration panel active init / change
   */
  useEffect(() => {
    if (isIntegrationKeyValid) {
      initSDK()
    }
  }, [configuration])

  /**
   * On button option change
   */
  useEffect(() => {
    if (selectedButtonOption)
      setConfiguration({
        ...configuration,
        buttonTheme: selectedButtonOption?.value,
      })
  }, [selectedButtonOption])

  /**
   * Watch for changes to the embed error listener
   */
  useEffect(() => {
    // If we have an error and no valid link, clear integration key and show config
    if (linkError) {
      if (!linkId) {
        setTimeout(() => {
          setConfiguration({
            ...configuration,
            integrationKey: '',
            linkId: '',
          })
        }, 2000)
      }
    }
  }, [linkError])

  /**
   * Clear embed
   */
  const clear = () => {
    setSdkLoaded(false)
    setLinkError(null)
    setLinkId('')
    setCurrentStep('')
    setSdkInitialized(false)
    setPolicies([])
  }

  const onStartOver = () => {
    setPolicies([])
    setSdkInitialized(false)
    initSDK()
  }

  /**
   * Clear embed and reset config
   */
  const resetToDefaults = () => {
    clear()
    setConfiguration(defaultConfiguration)
  }

  /**
   * Load SDK
   */
  const loadSDK = () => {
    return new Promise((resolve, reject) => {
      if (window.Covie) return resolve(window.Covie)

      // Create script tag
      const src = `/sdk/covie-access.js?v=1`
      const script = document.createElement('script')
      script.src = src
      script.async = true
      script.defer = true
      script.addEventListener('load', () => resolve(window.Covie), false)
      script.addEventListener(
        'error',
        () => reject(`Error loading Covie Link SDK from ${src}`),
        false
      )

      // Load script before first script tag
      const sibling = document.getElementsByTagName('script')[0]
      sibling.parentNode.insertBefore(script, sibling)
    })
  }

  /**
   * Initialize SDK
   */
  const initSDK = async () => {
    // if (sdkLoaded) return false

    // Begin loading sdk
    Covie.current = Covie.current ? Covie.current : await loadSDK()

    // Prevent hot reloading on dev from drawing multiple iframes
    // setSdkLoaded(true)

    // Init sdk with params
    const finalConfig = {
      ...configuration,
      embed: {
        ...configuration.embed,
        onInit: function (linkId, env) {
          console.log('ENV:', env)
          console.log('INIT:', linkId)
          setSdkInitialized(true)
          setLinkError(null)
          setLinkId(linkId)
        },
        onSuccess: function (linkId, policies) {
          console.log('SUCCESS:', linkId)
          console.log(policies)
          setPolicies(policies)
          Covie.current.access.lightbox.destroy()
        },
        onDocumentUpload: function (linkId, url) {
          console.log('DOCUMENT UPLOAD:', linkId, url)
        },
        onStepChange: function (linkId, step) {
          console.log('STEP:', step)
          setCurrentStep(step)
        },
        onLinkError: function (linkId, error) {
          console.log('ERROR:', error)
          setLinkError(error)
        },
        onIneligibleLink: function (linkId, policies) {
          console.log('INELIGIBLE LINK:', policies)
        },
        embedHost: window.location.protocol + '//' + window.location.host,
      },
    }

    // Create button
    Covie.current.access.button(finalConfig)
  }

  /**
   * Displays the main panel (demo, configuration or SDK code)
   */
  const renderPanel = () => {
    switch (demoState) {
      case 'CONFIG':
      default:
        return (
          <div className="flex-1">
            <div className="w-full p-8">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                <Input
                  fullwidth
                  label="Integration Key"
                  placeholder="integrationKey"
                  type="text"
                  name="integrationKey"
                  error={!isIntegrationKeyValid}
                  onChange={(val) =>
                    setConfiguration({
                      ...configuration,
                      embed: { ...configuration.embed, integrationKey: val },
                    })
                  }
                  value={configuration.embed.integrationKey}
                />
                <Input
                  fullwidth
                  label="Link ID"
                  placeholder="linkId"
                  type="text"
                  onChange={(val) =>
                    setConfiguration({
                      ...configuration,
                      embed: { ...configuration.embed, linkId: val },
                    })
                  }
                  value={configuration.embed.linkId}
                />
                <div>
                  <Select
                    label="Button style"
                    options={buttonOptions}
                    value={selectedButtonOption.value}
                    onChange={(selected) => {
                      setSelectedButtonOption(selected)
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="bg-gray-50 p-8">
              <div className="flex items-center justify-center">
                <div id="covie-demo" className={hideSDK ? 'hidden' : ''}></div>
                {!isIntegrationKeyValid && (
                  <div className="text-red-600">
                    Please enter a valid integration key
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      case 'CODE':
        return (
          <div>
            <div className="p-8">
              <div className="prose w-full max-w-none">
                <h2>Here is your code</h2>
                <p>
                  {`Copy and paste the code snippet below into your website before the closing <code>&lt;/body&gt;</code> tag. Make sure you move
                           the <code>covie-root</code> div element to where you want the "Link with Covie" button to render.`}
                </p>
                <pre>{`<div id="covie-root"></div>
<script>
    window.covieReady = function() {
        Covie.access.button({
            elementId: "covie-root",
            buttonSize: "md",
            buttonTheme: "${configuration.buttonTheme}",
            embed: {
               integrationKey: "${configuration.embed.integrationKey || ''}",${
                  embedHost
                    ? '\n               embedHost: "' + embedHost + '",'
                    : ''
                }
               linkId: ${
                 configuration.embed?.linkId
                   ? '"' + configuration.embed.linkId + '"'
                   : 'null'
               },
               onInit: function (linkId, environment) {},
               onSuccess: function (linkId, policies) {},
               onStepChange: function (linkId, step) {},
               onLinkError: function (linkId, error) {},
               onMissingProvider: function (linkId, searchTerm) {},
               onIneligibleLink: function (linkId, policies) {},
               onDocumentUpload: function (linkId, location) {},
               onClose: function () {},
            }
        })
    }
</script>
<script async defer src="https://access.covie.io/sdk/covie-access.js"></script>`}</pre>
              </div>
            </div>
            <div className="bg-gray-50 p-8">
              <div className="flex items-stretch justify-center space-x-2">
                <div id="covie-demo"></div>
                <div className="flex items-stretch">
                  <Button color="white" onClick={() => setDemoState('CONFIG')}>
                    Configure the embed
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )
    }
  }

  return (
    <div className="flex-1">
      <Header />

      <div className="relative py-4">
        {/* BG */}
        <div className="absolute left-0 top-0 h-64 w-full">
          <div id="intro-bg" className="h-full w-full"></div>
          <div className="absolute left-0 top-0 h-full w-full bg-gradient-to-t from-white"></div>
        </div>

        <div className="relative z-10 mx-auto flex justify-center">
          {/* Demo */}
          <div className="flex w-full flex-col space-y-12 py-12 md:py-24">
            {/* Text */}
            {!sdkInitialized && (
              <div className="mx-auto flex w-full max-w-3xl flex-col px-4 text-center">
                <h2 className="text-4xl font-bold text-gray-800 sm:text-5xl md:text-6xl">
                  See it in action
                </h2>
                <p className="mt-8 text-lg font-light text-gray-500 sm:text-xl md:text-2xl">
                  Use the embedded demo below to see how quickly and easily your
                  customers can link their policies within your existing
                  workflow.
                </p>

                <div className="space-y-4 pt-12 sm:flex sm:justify-center sm:space-x-4 sm:space-y-0">
                  <Button color="black" href="/docs">
                    See the documentation
                  </Button>
                  {demoState === 'CONFIG' ? (
                    <Button
                      color="purple"
                      disabled={!isIntegrationKeyValid}
                      onClick={() => {
                        setDemoState('CODE')
                      }}
                    >
                      View the code
                    </Button>
                  ) : (
                    <Button
                      color="purple"
                      onClick={() => {
                        setDemoState('CONFIG')
                      }}
                    >
                      Configure the embed
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Embed */}
            <div className="relative flex w-full flex-col sm:px-4">
              {policies.length > 0 ? (
                <CompletedLink policies={policies} onStartOver={onStartOver} />
              ) : (
                <div className="z-20 flex max-w-4xl justify-center self-center overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
                  {renderPanel(embedHost)}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  )
}

/**
 * Get environment arguments at build time
 */
export async function getStaticProps(context) {
  return {
    props: {
      demoIntegrationKey: process.env.DEMO_INTEGRATION_ID ?? null,
      embedHost: process.env.EMBED_HOST ?? null,
    },
  }
}
